import React, {
  forwardRef,
  useState,
  useEffect,
  HTMLAttributes,
  ChangeEvent,
} from "react";
import classNames from "classnames";
import {twMerge} from "tailwind-merge";
import {usePinInput} from "./usePinInput";
import {useRefStore} from "./useRefStore";

export interface PinInputProps extends HTMLAttributes<HTMLDivElement> {
  onComplete: (pin: string) => void;
}

export const InputField = forwardRef<
  HTMLInputElement | null,
  React.ComponentProps<"input">
>(({className, ...restProps}, inputRef) => {
  return (
    <input
      className={twMerge(
        classNames(
          "rounded-none focus:placeholder:text-transparent text-center",
          "outline:none border-b-2 border-gray-300 py-0.5 text-gray-500 text-2xl",
          "focus:outline-none focus:border-gray-500 transition-colors placeholder-gray-300",
        ),
        className,
      )}
      ref={inputRef}
      {...restProps}
      placeholder="0"
      type="text"
      inputMode="numeric"
    />
  );
});

export const PinInput: React.FC<PinInputProps> = ({
  children,
  className,
  onComplete,
}) => {
  const inputElementsCount = React.Children.count(children);
  const [focusIndex, setFocusIndex] = useState(0);
  const {refStore} = useRefStore(inputElementsCount);
  const {pin, setPin, setPinAtIndex} = usePinInput(inputElementsCount);

  useEffect(() => {
    if (focusIndex >= refStore.length) {
      checkFocusIndex();
    }
    refStore[focusIndex]?.current?.focus();
  }, [focusIndex, refStore, pin, onComplete]);

  const checkFocusIndex = () => {
    const emptyFieldIndex = pin.findIndex((item) => !item);
    if (emptyFieldIndex >= 0) {
      setFocusIndex(emptyFieldIndex);
      return;
    }
    setFocusIndex(focusIndex - 1);
    onComplete(pin.join(""));
    return;
  };

  const handlePaste = (evt: React.ClipboardEvent<HTMLInputElement>) => {
    evt.preventDefault();
    const copiedPin = evt.clipboardData.getData("text").replace(" ", "");
    updatedCopiedPin(copiedPin);
  };

  const updatedCopiedPin = (copiedPin: string) => {
    if (!isNaN(Number(copiedPin))) {
      const copiedPinArray = pin.map((val, ind) =>
        !!copiedPin[ind] ? copiedPin[ind] : val,
      );
      const indexToFocus = copiedPinArray.findIndex((val) => !val);
      setPin(copiedPinArray);
      setFocusIndex(indexToFocus >= 0 ? indexToFocus : inputElementsCount);
    }
  };

  const handleChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;
    if (value.length === inputElementsCount) {
      updatedCopiedPin(value);
      return;
    }
    if (isNaN(Number(value))) return;
    setPinAtIndex(value[value.length - 1], focusIndex);
    if (!value) return;
    setFocusIndex(focusIndex + 1);
  };

  const handleKeydown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
    if (pin[focusIndex]) return;
    if (evt.key !== "Backspace") return;
    if (focusIndex > 0) {
      setFocusIndex(focusIndex - 1);
      setPinAtIndex("", focusIndex - 1);
    }
  };

  return (
    <div
      className={twMerge(
        "flex w-fit max-w-sm gap-2 justify-between",
        className,
      )}
    >
      {React.Children.map<React.ReactNode, React.ReactNode>(
        children,
        (child, ind) => {
          if (!React.isValidElement(child)) return;
          return React.cloneElement(
            child as React.ReactElement<
              React.ComponentPropsWithoutRef<"input"> & {
                ref?: React.Ref<HTMLInputElement | null>;
              }
            >,
            {
              ref: refStore[ind],
              value: pin[ind],
              onFocus: () => setFocusIndex(ind),
              onPaste: handlePaste,
              onChange: handleChange,
              onKeyDown: handleKeydown,
              style: {
                width: `${Math.floor(100 / React.Children.count(children))}%`,
              },
            },
          );
        },
      )}
    </div>
  );
};

InputField.displayName = "InputField";
